import React, { useState } from "react";
import { NavLink as RRNavLink, useNavigate } from "react-router-dom";
import { NavLink } from "reactstrap";
import HeaderLanding from "./../Layout/HeaderLanding";
import UserProfileInput from "../component/UserProfileInput";
import UserProfilePhone from "../component/UserProfilePhone";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const Signup = () => {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    areaCode: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSignupRequest = async () => {
    const result = await Swal.fire({
      title: "Privacy Policy (Internal Testing Phase)",
      html: `
        <p><strong>MIRACLE PACE INC.</strong> is committed to protecting your privacy. During this internal testing phase, we collect limited personal information solely for testing purposes.</p>
        <p>All data is securely stored and will not be shared with third parties. We comply with the Personal Information Protection and Electronic Documents Act (PIPEDA).</p>
        <p>For inquiries, contact <a href="mailto:miraclepaceinc@outlook.com">miraclepaceinc@outlook.com</a>. A full Privacy Policy will be available upon public launch.</p>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "I Agree",
      cancelButtonText: "I Don't Agree",
      customClass: {
        popup: "text-left",
      },
    });

    if (result.isConfirmed) {
      const generatedCode = Math.floor(100000 + Math.random() * 900000);
      localStorage.setItem("generatedCode", generatedCode.toString());
      setStep(3);
      const message = `Welcome to Miracle Pace. Your verification code is ${generatedCode}, do not share it with anyone else.`;

      fetch("https://d593upkld8.execute-api.us-east-1.amazonaws.com/send-sms", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phoneNumber: formData.phoneNumber,
          message: message,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to send SMS");
          }
          return response.json();
        })
        .catch((error) => {
          console.error("SMS sending error:", error);
        });
    } else {
      Swal.fire({
        icon: "warning",
        title: "Signup Cancelled",
        text: "You must agree to the Privacy Policy to proceed.",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === 1) {
      if (
        !formData.firstName ||
        !formData.lastName ||
        !formData.phoneNumber ||
        !formData.areaCode ||
        !formData.email
      ) {
        alert("Please fill in all required fields.");
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      if (formData.password !== formData.confirmPassword) {
        alert("Passwords do not match.");
        return;
      }
      if (
        !formData.username ||
        !formData.password ||
        !formData.firstName ||
        !formData.lastName ||
        !formData.email
      ) {
        alert("Please fill in all required fields.");
        return;
      }
      await handleSignupRequest();
      setStep(3);
      return;
    }

    if (step === 3) {
      const storedCode = localStorage.getItem("generatedCode");
      if (verificationCode === storedCode) {
        alert("Admin code verified successfully!");
        const userData = {
          firstname: formData.firstName,
          lastname: formData.lastName,
          areaCode: formData.areaCode,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          username: formData.username,
          password: formData.password,
        };
        try {
          const response = await fetch(`${apiUrl}/user/signup`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(userData),
          });
          if (response.ok) {
            alert(t("signup.success.label"));

            fetch(`${apiUrl}/aws/email/signup`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                email: formData.email,
                firstName: formData.firstName,
              }),
            }).catch((err) => {
              console.error("Email send failed", err);
            });

            navigate("/signin");
          } else {
            alert(t("signup.failed.label"));
          }
        } catch (error) {
          alert(t("signup.failed.label"));
        }
      } else {
        alert(t("sms.code.not.correct.label"));
      }
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <>
            <div className="row">
              <div className="col-12">
                <UserProfileInput
                  fieldLabel={t("first.name.label")}
                  placeholder="Enter your first name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12">
                <UserProfileInput
                  fieldLabel={t("last.name.label")}
                  placeholder="Enter your last name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12">
                <UserProfilePhone
                  fieldLabel={t("phone.number.label")}
                  placeholderArea="Area Code"
                  placeholderPhone="Enter your phone number"
                  valueArea={formData.areaCode}
                  valuePhone={formData.phoneNumber}
                  onChangeArea={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      areaCode: e.target.value,
                    }))
                  }
                  onChangePhone={(e) => handleChange(e)}
                />
              </div>
              <div className="col-12">
                <UserProfileInput
                  fieldLabel={t("email.label")}
                  placeholder="Enter your email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className="form-group">
              <label>{t("username.label")}</label>
              <input
                type="text"
                className="form-control"
                placeholder="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>{t("password.label")}</label>
              <input
                type="password"
                className="form-control"
                placeholder="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>{t("confirm.password.label")}</label>
              <input
                type="password"
                className="form-control"
                placeholder="Confirm Password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className="form-group">
              <label>{t("enter.verification.code.label")}</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Admin Code"
                name="smsCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <HeaderLanding />
      <div className="authincation section-padding">
        <div className="container h-100">
          <div className="row justify-content-center h-100 align-items-center">
            <div className="col-xl-4 col-md-6">
              <div className="auth-form card">
                <div className="card-header justify-content-center">
                  <h4 className="card-title">
                    {t("sign.up.your.account.label")}
                  </h4>
                </div>
                <div className="card-body">
                  <form method="post" onSubmit={handleSubmit}>
                    {renderStep()}
                    <div className="text-center mt-4 d-flex justify-content-between">
                      {step > 1 && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setStep((s) => s - 1)}
                        >
                          {t("back.label")}
                        </button>
                      )}
                      {step < 3 ? (
                        step === 2 ? (
                          <button type="submit" className="btn btn-primary">
                            {t("send.sms.label")}
                          </button>
                        ) : (
                          <button type="submit" className="btn btn-primary">
                            {t("next.label")}
                          </button>
                        )
                      ) : (
                        <button
                          type="submit"
                          className="btn btn-success btn-block"
                        >
                          {t("verify.sms.label")}
                        </button>
                      )}
                    </div>
                  </form>
                  <div className="new-account mt-3">
                    <p>
                      {t("already.have.account.label")}{" "}
                      <NavLink
                        className="text-primary d-inline-block"
                        to="/signin"
                        tag={RRNavLink}
                      >
                        {t("signIn.label")}
                      </NavLink>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg_icons"></div>
    </div>
  );
};

export default Signup;
